import React, { useState, useEffect } from 'react';
import { Trophy, Globe, Users, Flame, Search, Copy, Check, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import RankBadge from '../components/RankBadge';
import StreakBadge from '../components/StreakBadge';
import AvatarStack from '../components/AvatarStack';
import GlassCard from '../components/GlassCard';
import { useToast } from '../components/Toast';
import { AVATARS, copyToClipboard } from '../lib/utils';
import { leaderboardApi } from '../lib/apiClient';

export default function LeaderboardPage() {
  const { user } = useAuthStore();
  const { 
    groupId, 
    groupName, 
    members, 
    globalLeaderboard: mockGlobalLeaderboard,
    weeklyMinimumHours
  } = useGroupStore();
  
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('group'); // 'group' | 'global'
  const [copied, setCopied] = useState(false);

  // Global Tab States
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalPage, setGlobalPage] = useState(1);
  const [globalData, setGlobalData] = useState([]);
  
  // Sort members by study hours (descending)
  const sortedMembers = [...members].sort((a, b) => b.weeklyHours - a.weeklyHours);

  // Copy Group ID
  const handleCopyId = async () => {
    if (!groupId) return;
    const success = await copyToClipboard(groupId);
    if (success) {
      setCopied(true);
      toast.success('Group ID copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy Group ID');
    }
  };

  // Poll/fetch Global Leaderboard data
  useEffect(() => {
    let active = true;
    const fetchGlobal = async () => {
      try {
        const res = await leaderboardApi.getGlobal(globalPage).catch(() => null);
        if (active && res) {
          setGlobalData(res);
        } else if (active) {
          // Fallback to mock global leaderboard
          setGlobalData(mockGlobalLeaderboard);
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (activeTab === 'global') {
      fetchGlobal();
    }
    return () => { active = false; };
  }, [activeTab, globalPage, mockGlobalLeaderboard]);

  // Filter global leaderboard by search query
  const filteredGlobal = globalData.filter(g => 
    g.groupName.toLowerCase().includes(globalSearch.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full p-6 md:p-8 overflow-y-auto space-y-6">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="heading text-3xl font-extrabold text-white tracking-wide">
            Colosseum of Scholars
          </h1>
          <p className="text-white/40 text-sm">
            Fame is forged in dedication. Ascend past your peers.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="relative flex rounded-xl bg-white/5 border border-white/10 p-1 self-start md:self-center">
          <button
            onClick={() => setActiveTab('group')}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
              activeTab === 'group' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <Users size={14} />
            <span>My Group</span>
          </button>
          <button
            onClick={() => setActiveTab('global')}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
              activeTab === 'global' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30' : 'text-white/50 hover:text-white/80'
            }`}
          >
            <Globe size={14} />
            <span>Global</span>
          </button>
        </div>
      </div>

      {/* Content Container */}
      {activeTab === 'group' ? (
        
        /* ── MY GROUP LEADERBOARD ── */
        <div className="space-y-4">
          {/* Header Card with Group Info */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 backdrop-blur-md">
            <div>
              <h2 className="heading text-lg font-bold text-white tracking-wide">{groupName}</h2>
              <p className="text-white/30 text-xs mt-0.5">Weekly min study: {user?.weeklyGoalHours || 20} hours</p>
            </div>
            
            {/* Copy Group ID Group Box */}
            <div className="flex items-center gap-2 bg-white/5 border border-white/5 px-3 py-1.5 rounded-lg self-start sm:self-center">
              <span className="text-[11px] font-mono text-white/50 font-bold uppercase tracking-wider">ID:</span>
              <span className="text-xs font-mono text-indigo-400 font-bold select-all">{groupId}</span>
              <button
                onClick={handleCopyId}
                className="text-white/40 hover:text-indigo-400 transition-colors p-1"
                title="Copy Group ID"
              >
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Leaderboard Table / List */}
          <div className="space-y-3">
            {sortedMembers.map((member, index) => {
              const rank = index + 1;
              const isCurrentUser = member.id === user?.id;
              
              // Calculate weekly goal completion percentage
              const goalCompletionPct = Math.min((member.weeklyHours / weeklyMinimumHours) * 100, 100);
              const isGoalCompleted = member.weeklyHours >= weeklyMinimumHours;
              
              const avatarColor = AVATARS[member.avatar % AVATARS.length]?.bg || '#6366f1';
              const avatarEmoji = AVATARS[member.avatar % AVATARS.length]?.emoji || '🎓';

              // Weekly change formatting
              const isChangePositive = member.weeklyChange >= 0;

              return (
                <div
                  key={member.id}
                  className={`relative flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl transition-all duration-300 lift ${
                    isCurrentUser 
                      ? 'bg-indigo-500/10 border-2 border-indigo-500/60 shadow-lg shadow-indigo-500/10' 
                      : 'bg-white/5 border border-white/10'
                  } animate-slide-up`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Rank Badge */}
                    <RankBadge rank={rank} />

                    {/* Avatar & Name */}
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-lg shadow-inner relative flex-shrink-0"
                        style={{ background: avatarColor, boxShadow: `0 0 0 2px #090e1a, 0 0 0 4px ${avatarColor}30` }}
                      >
                        {avatarEmoji}
                        {member.isOnline && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-navy-900 rounded-full animate-pulse" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white truncate max-w-[120px] md:max-w-none">
                          {member.name} {isCurrentUser && <span className="text-[10px] text-indigo-400 font-bold ml-1 uppercase">(You)</span>}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-white/30 text-xs capitalize">
                            {member.role === 'vice-leader' || member.role === 'vice_leader' ? 'Vice-Leader' : member.role || 'Member'}
                          </span>
                          <span className="text-white/20 text-[10px]">•</span>
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isGoalCompleted ? 'text-emerald-400' : 'text-amber-500'}`}>
                            {goalCompletionPct.toFixed(0)}% Goal
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Weekly hours bar (Desktop only) */}
                    <div className="hidden md:block flex-1 max-w-sm ml-6">
                      <div className="w-full bg-white/5 border border-white/5 h-2.5 rounded-full overflow-hidden" title={`${goalCompletionPct.toFixed(1)}% of weekly goal`}>
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            isGoalCompleted 
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]' 
                              : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                          }`}
                          style={{ width: `${goalCompletionPct}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right items (hours, streak, change) */}
                  <div className="flex items-center justify-between md:justify-end gap-6 border-t border-white/5 pt-3 md:pt-0 md:border-none">
                    
                    {/* Streak Flame */}
                    <div className="flex items-center gap-1.5" title={`${member.streak} day streak`}>
                      <Flame size={16} className={`${member.streak > 0 ? 'text-amber-500 animate-pulse' : 'text-white/20'}`} />
                      <span className={`text-xs font-mono font-bold ${member.streak > 0 ? 'text-white/80' : 'text-white/20'}`}>
                        {member.streak}d
                      </span>
                    </div>

                    {/* Change indicator */}
                    <div className="flex items-center gap-1 font-mono text-xs" title="Hours delta vs last week">
                      {isChangePositive ? (
                        <>
                          <TrendingUp size={14} className="text-green-400" />
                          <span className="text-green-400">+{member.weeklyChange.toFixed(1)}h</span>
                        </>
                      ) : (
                        <>
                          <TrendingDown size={14} className="text-red-400" />
                          <span className="text-red-400">{member.weeklyChange.toFixed(1)}h</span>
                        </>
                      )}
                    </div>

                    {/* Total Hours */}
                    <div className="text-right">
                      <span className="text-sm font-bold text-white font-mono">{member.weeklyHours.toFixed(1)}</span>
                      <span className="text-white/40 text-[10px] uppercase ml-1 tracking-wider font-semibold">/ {weeklyMinimumHours}h</span>
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        
        /* ── GLOBAL LEADERBOARD ── */
        <div className="space-y-4">
          
          {/* Search bar */}
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-3.5 top-3.5 text-white/30" />
            <input
              type="text"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder="Search approved public leagues..."
              className="w-full rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/25 outline-none transition-all duration-200 border border-white/10"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            />
          </div>

          {/* Global groups rows */}
          <div className="space-y-3">
            {filteredGlobal.length === 0 ? (
              <div className="text-white/20 text-xs py-8 text-center bg-white/5 rounded-xl border border-white/10">
                No matching study groups found.
              </div>
            ) : (
              filteredGlobal.map((group, index) => {
                const isUserGroup = group.groupName === groupName;
                
                return (
                  <div
                    key={group.groupName}
                    className={`relative flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl transition-all duration-300 lift ${
                      isUserGroup 
                        ? 'bg-indigo-500/10 border border-indigo-500/60 shadow-lg shadow-indigo-500/10' 
                        : 'bg-white/5 border border-white/10'
                    } animate-slide-up`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      {/* Global Rank number */}
                      <div className="w-8 text-center text-sm font-bold text-white/40 font-mono">
                        #{group.rank}
                      </div>

                      <div>
                        <h3 className="text-sm font-bold text-white truncate max-w-[200px] sm:max-w-none">
                          {group.groupName} {isUserGroup && <span className="text-[10px] text-indigo-400 font-bold ml-1 uppercase">(Your Group)</span>}
                        </h3>
                        <div className="flex items-center gap-1.5 text-white/30 text-xs mt-0.5">
                          <Users size={12} />
                          <span>{group.memberCount} / 15 members</span>
                        </div>
                      </div>
                    </div>

                    {/* Right column: top members avatar stack + average hours */}
                    <div className="flex items-center justify-between sm:justify-end gap-6 border-t border-white/5 pt-3 sm:pt-0 sm:border-none">
                      {/* Avatar Stack of top studying members */}
                      <div className="hidden sm:block flex-shrink-0">
                        <AvatarStack avatarIndices={group.topMembers} maxShow={3} size={28} />
                      </div>

                      {/* Average Hours */}
                      <div className="text-right">
                        <span className="text-sm font-bold text-white font-mono">{group.avgHours.toFixed(1)}</span>
                        <span className="text-white/40 text-[10px] uppercase ml-1 tracking-wider font-semibold">avg hrs</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

    </div>
  );
}
